import { useCallback, useEffect, useState } from "react";
import { TableProps } from "@amzn/awsui-components-react/polaris/table";

/**
 * How a column is sized in a fitted, resizable table:
 * - `width`: fixed.
 * - `fit`: a share of the table width, clamped to [min, max] (wide on a desktop, narrower on a laptop).
 * - `fill`: whatever is left after the others (at least `minWidth`).
 * A width the user drags always wins and is kept per browser.
 */
export type FittedColumn<T> = TableProps.ColumnDefinition<T> & {
  fit?: { share: number; min: number; max: number };
  fill?: boolean;
};

/** Before the container is measured (and in tests). */
const FALLBACK_WIDTH = 1400;
/** Cell padding/borders the widths don't include. */
const CHROME = 40;
const DEFAULT_FILL_MIN = 200;

export const fitColumns = <T>(
  columns: FittedColumn<T>[],
  saved: Record<string, number>,
  containerWidth: number
): Record<string, number> => {
  const avail = (containerWidth || FALLBACK_WIDTH) - CHROME;
  const widths: Record<string, number> = {};
  let left = avail;
  columns.forEach((c) => {
    const id = c.id ?? "";
    let w: number | undefined = saved[id];
    if (w === undefined && c.fit)
      w = Math.round(
        Math.min(c.fit.max, Math.max(c.fit.min, avail * c.fit.share))
      );
    else if (w === undefined && !c.fill) w = c.width as number | undefined;
    if (typeof w === "number") {
      widths[id] = w;
      left -= w;
    }
  });
  columns.forEach((c) => {
    const id = c.id ?? "";
    if (c.fill && widths[id] === undefined)
      widths[id] = Math.max(
        Number(c.minWidth ?? DEFAULT_FILL_MIN),
        Math.floor(left)
      );
  });
  return widths;
};

const load = (key: string): Record<string, number> => {
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(key) ?? "{}"
    ) as Record<string, unknown> | null;
    const out: Record<string, number> = {};
    Object.entries(parsed ?? {}).forEach(([id, w]) => {
      if (typeof w === "number" && Number.isFinite(w) && w > 0) out[id] = w;
    });
    return out;
  } catch {
    return {};
  }
};

/**
 * Resizable table columns that fit the container: pass the result's props to <Table>.
 * Remounts (via `key`) when the container width moves noticeably, since Cloudscape reads widths
 * once per mount — so collapsing the side nav or resizing the window re-fits the columns.
 */
const useFittedColumns = <T>(
  storageKey: string,
  columns: FittedColumn<T>[]
) => {
  const [saved, setSaved] = useState(() => load(storageKey));
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.round(entry.contentRect.width))
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);

  const fitted = fitColumns(columns, saved, width);
  const columnDefinitions: TableProps.ColumnDefinition<T>[] = columns.map(
    ({ fit: _fit, fill: _fill, ...c }) => ({
      ...c,
      width: fitted[c.id ?? ""] ?? c.width,
    })
  );

  const onColumnWidthsChange = useCallback<
    NonNullable<TableProps<T>["onColumnWidthsChange"]>
  >(
    ({ detail }) => {
      // Every column's rendered width is reported; keep only the one the user dragged.
      let dragged: { id: string; w: number; delta: number } | undefined;
      detail.widths.forEach((w, i) => {
        const c = columnDefinitions[i];
        if (!c?.id) return;
        const delta = Math.abs(w - (Number(c.width) || w));
        if (delta > 1 && (!dragged || delta > dragged.delta))
          dragged = { id: c.id, w, delta };
      });
      if (!dragged) return;
      const next = { ...saved, [dragged.id]: dragged.w };
      setSaved(next);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Storage full or disabled: the width still applies for this session.
      }
    },
    [columnDefinitions, saved, storageKey]
  );

  return {
    containerRef: setEl,
    tableKey: `w${Math.round(width / 40)}`,
    tableProps: {
      columnDefinitions,
      resizableColumns: true,
      onColumnWidthsChange,
    },
  };
};

export default useFittedColumns;
